import { setUser as sentrySetUser } from "@sentry/browser";
import { bypassCognito } from "common/utils/context";
import * as cognito from "./cognito";
import * as cookie from "./cookie";

// Deduplicates concurrent getToken() calls so that only one Cognito
// session refresh is in-flight at a time. Without this, simultaneous
// calls (e.g. multiple SWR hooks firing on navigation) can race on
// the refresh-token exchange and produce transient 401s.
let inflight: Promise<string | null> | null = null;

export async function getToken(): Promise<string | null> {
  if (bypassCognito()) {
    const userCookie = cookie.get(cognito.HIGHARC_USER_COOKIE);
    if (!userCookie) {
      return null;
    }

    const { jwtToken } = JSON.parse(userCookie);
    return jwtToken;
  }

  if (inflight) {
    return inflight;
  }

  inflight = resolveToken();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

async function resolveToken(): Promise<string | null> {
  // Try the standard Cognito SDK session first (password-auth users)
  const user = cognito.getUser();
  if (user) {
    try {
      const username = user.getUsername();
      sentrySetUser({ email: username });

      const session = await cognito.getSession(user);

      const jwtToken = session.getIdToken().getJwtToken();
      cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ username, jwtToken }));
      return jwtToken;
    } catch (e) {
      console.error("Unable to establish cognito session for user");
    }
  }

  // SSO users authenticate via Hosted UI — no Cognito SDK session in this tab.
  // Fall back to the shared auth cookie (works across tabs) before refreshing.
  const cookieToken = cognito.getValidJwtFromUserCookie();
  if (cookieToken) {
    const email = cognito.getEmailFromJwt(cookieToken);
    if (email) {
      sentrySetUser({ email });
    }
    return cookieToken;
  }

  // Fall back to SSO refresh token (federated / Google SSO users)
  const ssoToken = await cognito.refreshSSOToken();
  if (ssoToken) {
    const email = cognito.getEmailFromJwt(ssoToken) ?? "";
    sentrySetUser({ email });
    cookie.set(cognito.HIGHARC_USER_COOKIE, JSON.stringify({ cognitoName: email, jwtToken: ssoToken }));
    return ssoToken;
  }

  sentrySetUser(null);
  return null;
}
